import { useState, type FormEvent } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { resetOpsCache } from "@/lib/stubs";
import type { LoginResult, Me } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { TwoStepForm } from "./TwoStepForm";

/** Sign-in: email + password, then a 6-digit authenticator code (2FA is mandatory). */
export function LoginPage() {
  const [step, setStep] = useState<LoginResult | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { next } = useSearch({ from: "/login" });

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setStep(await api<LoginResult>("/api/v1/auth/login", { json: { email, password } }));
      setPassword("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const done = (user: Me) => {
    // Ask the server for its operations again now that we are signed in (an earlier, signed-out answer is stale).
    resetOpsCache();
    void qc.invalidateQueries({ queryKey: ["has-op"] });
    qc.setQueryData(["me"], user);
    navigate({ to: next && next.startsWith("/") && next !== "/login" ? next : "/" });
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="space-y-4 py-6">
          <div>
            <h1 className="text-lg font-semibold">CREU CRM</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">{step ? "Two-step verification" : "Sign in"}</p>
          </div>
          {!step && (
            <form onSubmit={submitPassword} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                Continue
              </Button>
              {error && (
                <p role="alert" className="text-sm text-[var(--color-destructive)]">
                  {error}
                </p>
              )}
            </form>
          )}
          {step && <TwoStepForm step={step} onDone={done} onExpired={() => setStep(null)} />}
        </CardContent>
      </Card>
    </div>
  );
}
